<template>
  <div id="board">
    <div class="page-title-area bg-6" data-aos="fade-up" data-aos-delay="0" data-aos-duration="1000">
      <div class="container">
        <div class="page-title-content">
          <h2>Our Board</h2>
          <ul>
            <li class="active">Meet PeaceJam's Directors</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Executive Director -->
    <section class="ed-section pt-100 pb-70" data-aos="fade-up" data-aos-delay="100" data-aos-duration="1000">
      <div class="container">
        <div class="row align-items-start">
          <div class="col-lg-5">
            <div class="ed-img">
              <img src="../assets/img/team/lauren-coffaro.jpg" alt="Lauren Coffaro, Executive Director and CEO" />
            </div>
          </div>
          <div class="col-lg-7">
            <div class="ed-content">
              <h2>Our Executive Director &amp; CEO</h2>
              <span class="ed-name">Lauren Coffaro</span>
              <p>
                Lauren Coffaro is the Executive Director and CEO of the PeaceJam Foundation. Since joining PeaceJam in
                2016, she has lived its youth leadership pipeline firsthand. She began as a volunteer mentor in the UK.
                She then worked alongside 1992 Nobel Peace Laureate Rigoberta Menchú Tum and her foundation to grow
                PeaceJam in Guatemala, and went on to support programs across Latin America. She became Director of
                Global Programs in 2018. In 2024, she stepped in as Interim Executive Director to guide PeaceJam's
                transition from a founder-led organization to one built for long-term sustainability. She was named
                permanent Executive Director in March 2025.
              </p>
              <p>
                Lauren's career has spanned cultural diplomacy and peace education, always centered on youth. Early on,
                she oversaw international citizen exchange programs for youth career development under the auspices of
                the U.S. Department of State. She later held a visiting professorship in Colombia, leading workshops
                and lectures for youth on peace and post-conflict integration after the signing of the peace accords.
              </p>
              <p>
                Lauren earned a Master's in Peace Studies with Distinction from the University of Bradford as a Rotary
                Peace Fellow. She also earned a Bachelor's in Communications, cum laude, from Northwestern University.
                She holds an executive certificate in Strategic Perspectives in Nonprofit Management from Harvard
                Business School.
              </p>
              <p>
                Working alongside Nobel Peace Laureates has given Lauren a profound appreciation for compassionate
                leadership. Collaborating with PeaceJam's global teams has taught her to lead with humility and an open
                mind. And PeaceJam's extraordinary young leaders have shown her that youth solutions deserve to be
                taken seriously. She uses her platform to advocate for youth inclusion in peacebuilding and to open
                doors for the young people who can and will change the world for the better.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Board Members -->
    <section class="board-section pb-70" data-aos="fade-up" data-aos-delay="300" data-aos-duration="1000">
      <div class="container">
        <div class="section-title">
          <h2>Board of Directors</h2>
        </div>

        <div class="loading" style="height: 400px" v-if="board_data === undefined && !board_error">
          <Loading />
        </div>

        <div class="row" v-else-if="board_data && board_data.data.length">
          <div class="col-lg-6" v-for="member in board_data.data" :key="member.id">
            <div class="board-card">
              <div class="board-card-img">
                <DefaultImage :id="member.image" :width="'100%'" :height="'100%'" />
              </div>
              <div class="board-card-body">
                <h3>{{ member.name }}</h3>
                <span class="role" v-if="member.apointee">{{ member.apointee }}</span>
                <template v-if="member.bio">
                  <p class="bio">{{ isLong(member.bio) ? truncate(member.bio) : member.bio }}</p>
                  <button v-if="isLong(member.bio)" type="button" class="show-more-btn" @click="openBio(member)">
                    Show more
                  </button>
                </template>
                <p class="bio is-placeholder" v-else>Bio coming soon.</p>
              </div>
            </div>
          </div>
        </div>

        <p class="empty-note" v-else>No board members to display yet.</p>
      </div>
    </section>

    <!-- Governance -->
    <section class="governance-section pb-70">
      <div class="container">
        <div class="section-title">
          <h2>Our Governance Model</h2>
        </div>
        <div class="governance-card">
          <p>
            PeaceJam is guided by a Board of Directors that unites principled vision with professional expertise. Each
            Nobel Peace Laureate member appoints one representative to the Board, giving the peacemakers who inspire
            our work a direct voice in how it grows. They are joined by up to five independent directors, chosen for
            the skills that strong oversight demands. Each independent director also makes a personal fundraising
            commitment, contributing or securing financial support to sustain PeaceJam's work.
          </p>
          <p>
            Our Board is further strengthened by PeaceJam's Strategic Advisors, a dedicated advisory body that supports
            good governance with insight into operations, legal compliance, program expertise, and education. While not
            board members, our Strategic Advisors ensure that decisions at every level are informed by deep, practical
            knowledge.
          </p>
          <p class="mb-0">
            The result is a governance structure rooted in the wisdom of the world's leading peacemakers and
            accountable to the highest standards of stewardship, ensuring every investment in PeaceJam is well governed
            and built to last.
          </p>
        </div>
      </div>
    </section>

    <!-- Financial Information -->
    <section id="financial" >
      <div class="container">
        <div class="row">
          <div class="col-12">
              <h2>Financial Information</h2>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4 document-card">
            <div class="row">
              <div class="col-3">
                <img src="../assets/img/financial/PeaceJamForm990.png" width="60px"/>
              </div>
              <div class="col-8">
                <p>PeaceJam Form 990</p>
                <a href="https://billionacts.blob.core.windows.net/billion-act-assets/PeaceJam/990%20Informational%20Return%20For%20PeaceJam.pdf" target="_blank" rel="noreferrer" class="events-btn-one">
                  <i class="flaticon-curve-arrow"></i> View
                </a>
              </div>
            </div>
          </div>
          <div class="col-md-4 document-card">
            <div class="row">
              <div class="col-3">
                <img src="../assets/img/financial/AnnualReport.png" width="60px"/>
              </div>
              <div class="col-8">
                <p>PeaceJam Annual Report</p>
                <a href="https://billionacts.blob.core.windows.net/billion-act-assets/PeaceJam/PeaceJam%20Annual%20Report.pdf" target="_blank" rel="noreferrer" class="events-btn-one">
                  <i class="flaticon-curve-arrow"></i> View
                </a>
              </div>
            </div>
          </div>
          <div class="col-md-4 document-card">
            <div class="row">
              <div class="col-3">
                <img src="../assets/img/financial/AuditedFinancials.png" width="60px"/>
              </div>
              <div class="col-8">
                <p>Audited Financials</p>
                <a href="https://billionacts.blob.core.windows.net/billion-act-assets/PeaceJam/Audit%20Report%20for%20PeaceJam%20Foundation.pdf" target="_blank" rel="noreferrer" class="events-btn-one">
                  <i class="flaticon-curve-arrow"></i> View
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- End of Financial -->

    <modal name="board-bio" height="auto" width="600px" :scrollable="true" :adaptive="true">
      <div class="board-modal">
        <div class="close-btn" @click="$modal.hide('board-bio')"><i class="fa fa-close"></i></div>
        <div class="board-modal-head">
          <div class="board-modal-img">
            <DefaultImage :id="modal_data.image" :width="'120px'" :height="'140px'" />
          </div>
          <div class="board-modal-title">
            <h3>{{ modal_data.name }}</h3>
            <span class="role" v-if="modal_data.apointee">{{ modal_data.apointee }}</span>
          </div>
        </div>
        <p class="board-modal-bio white-space-pre-wrap">{{ modal_data.bio }}</p>
      </div>
    </modal>
  </div>
</template>

<script>
  import api from "../services/api";
  import DefaultImage from "../components/DefaultImage";
  import Loading from "../components/Loading";
  import useSWRV from "swrv";

  export default {
    name: "Board",
    components: { Loading, DefaultImage },
    data() {
      return {
        modal_data: {},
        maxBioLength: 220
      };
    },
    methods: {
      isLong(text) {
        return !!text && text.length > this.maxBioLength;
      },
      truncate(text) {
        return text.slice(0, this.maxBioLength).trimEnd() + "\u2026";
      },
      openBio(member) {
        this.modal_data = member;
        this.$modal.show("board-bio");
      }
    },
    setup() {
      const {
        data: board_data,
        error: board_error,
        isValidating: board_isValidating
      } = useSWRV(() => `items/board_members?status=published&sort=sort`, api.fetcher);

      return {
        board_data,
        board_error,
        board_isValidating
      };
    },
    mounted() {
      window.scrollTo(0, 0);
    }
  };
</script>

<style scoped lang="scss">
  $card-bg: #34BDD5;
  $accent: #ed272a;

  .page-title-area .page-title-content ul .active {
    font-size: 20px;
  }
  .page-title-area {
    background-image: url("../assets/img/team/team.png");
    background-position: center;
    background-size: cover;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;
      background-color: rgba(0, 0, 0, 0.35);
    }

    .container {
      position: relative;
      z-index: 2;
    }
  }

  @media (max-width: 767px) {
    .page-title-area {
      padding-top: 100px;
      padding-bottom: 60px;
    }
  }

  .section-title {
    margin-bottom: 40px;
    text-align: center;
  }

  .board-card {
    background-color: $card-bg;
    color: #fff;
    padding: 30px;
    margin-bottom: 30px;
    border-radius: 6px;
    height: calc(100% - 30px);
    overflow: hidden;

    .board-card-img {
      float: left;
      width: 160px;
      height: 190px;
      margin: 0 22px 14px 0;
      overflow: hidden;
      border-radius: 4px;
      background-color: #2aa7bd;

      ::v-deep .default-img,
      ::v-deep .default-img img {
        width: 100% !important;
        height: 100% !important;
      }

      ::v-deep img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .board-card-body {
      h3 {
        color: #fff;
        font-size: 22px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        margin-bottom: 6px;
      }

      .role {
        display: block;
        color: #0c4653;
        font-weight: 600;
        text-transform: uppercase;
        font-size: 14px;
        letter-spacing: 0.03em;
        margin-bottom: 16px;
      }

      .bio {
        color: rgba(255, 255, 255, 0.82);
        font-size: 14px;
        line-height: 1.7;
        margin-bottom: 0;
      }
    }
  }

  .show-more-btn {
    display: inline-block;
    margin-top: 12px;
    padding: 0;
    background: transparent;
    border: none;
    color: #fff;
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    cursor: pointer;
    border-bottom: 1px solid rgba(255, 255, 255, 0.7);
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.75;
    }
  }

  .board-modal {
    padding: 34px 30px 30px;
    position: relative;

    .close-btn {
      position: absolute;
      top: 14px;
      right: 14px;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      border: 2px solid #ccc;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #444;

      &:hover {
        border-color: $card-bg;
        color: $card-bg;
      }
    }

    .board-modal-head {
      display: flex;
      align-items: center;
      gap: 18px;
      margin-bottom: 20px;

      .board-modal-img {
        flex: 0 0 auto;
        width: 120px;
        height: 140px;
        overflow: hidden;
        border-radius: 4px;
        background-color: #eee;

        ::v-deep .default-img,
        ::v-deep .default-img img,
        ::v-deep img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover;
        }
      }

      .board-modal-title {
        h3 {
          font-size: 22px;
          font-weight: 700;
          text-transform: uppercase;
          margin-bottom: 6px;
          color: #1a1b1e;
        }

        .role {
          color: $card-bg;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 13px;
          letter-spacing: 0.03em;
        }
      }
    }

    .board-modal-bio {
      color: #555;
      font-size: 14px;
      line-height: 1.75;
      margin-bottom: 0;
    }

    .white-space-pre-wrap {
      white-space: pre-wrap;
    }
  }

  @media (max-width: 575px) {
    .board-modal .board-modal-head {
      flex-direction: column;
      text-align: center;
    }
  }

  .ed-img {
    margin-bottom: 30px;

    img {
      width: 100%;
      height: auto;
      display: block;
    }
  }

  @media (max-width: 991px) {
    .ed-img {
      max-width: 280px;
      margin-left: auto;
      margin-right: auto;

      img {
        height: 320px;
        object-fit: cover;
        object-position: top;
      }
    }
  }

  .ed-content {
    h2 {
      color: #123b8f;
      font-size: 38px;
      font-weight: 800;
      margin-bottom: 22px;
    }

    .ed-name {
      display: block;
      color: #123b8f;
      font-weight: 600;
      font-size: 17px;
      margin-bottom: 18px;

      em {
        color: #6d6a63;
        font-style: normal;
        font-weight: 400;
      }
    }

    p {
      color: #6d6a63;
      font-size: 16px;
      line-height: 1.9;
      margin-bottom: 20px;
    }
  }

  @media (max-width: 991px) {
    .ed-content h2 {
      font-size: 30px;
    }
  }

  .governance-card {
    background-color: #f6f6f6;
    border-left: 4px solid $accent;
    padding: 26px 30px;
    border-radius: 4px;
  }

  #financial {
    ::v-deep .document-card {
      .row {
        align-items: center;
      }
    }
  }

  @media (max-width: 575px) {
    #financial {
      ::v-deep .document-card {
        .row {
          flex-direction: column;
          text-align: center;
        }

        .col-3 {
          max-width: 100%;
          margin-bottom: 10px;
        }

        .col-8 {
          max-width: 100%;
        }
      }
    }
  }

  .is-placeholder {
    opacity: 0.75;
    font-style: italic;
  }

  .empty-note {
    text-align: center;
    color: #999;
  }

  @media (max-width: 575px) {
    .ed-content h2 {
      font-size: 24px;
    }

    .ed-content p {
      font-size: 15px;
      line-height: 1.7;
    }

    .governance-card {
      padding: 20px;
    }

    .board-card {
      padding: 24px;
      text-align: center;

      .board-card-img {
        float: none;
        width: 150px;
        height: 180px;
        margin: 0 auto 18px;
      }

      .board-card-body .role {
        margin-bottom: 12px;
      }
    }
  }
</style>
